import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import AuthModal from "@/components/AuthModal";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { Trash2, Flag } from "lucide-react";

export default function Comments({ shortId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [authOpen, setAuthOpen] = useState(false);

  const load = () => api.get(`/calls/${shortId}/comments`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [shortId]);

  const submit = async () => {
    if (!user) return setAuthOpen(true);
    if (!text.trim()) return;
    try {
      await api.post(`/calls/${shortId}/comments`, { text });
      setText(""); load();
    } catch { toast.error("Could not post comment"); }
  };

  const remove = async (id) => { await api.delete(`/comments/${id}`); load(); };
  const report = async (id) => {
    await api.post("/reports", { target_type: "comment", target_id: id, reason: "spam" });
    toast.success("Reported. Thanks.");
  };

  return (
    <div data-testid="comments-section">
      <h3 className="font-display text-xl font-bold mb-3">Comments ({items.length})</h3>
      <div className="flex gap-2 mb-5">
        <Textarea data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)}
          onClick={() => !user && setAuthOpen(true)} placeholder={user ? "Add a comment…" : "Sign in to comment"}
          rows={2} className="border-2 border-ink resize-none" />
        <button data-testid="comment-submit" onClick={submit}
          className="px-4 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm self-stretch">Post</button>
      </div>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className="border-2 border-ink bg-white p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="w-6 h-6 border-2 border-ink">
                  <AvatarImage src={c.author?.avatar_url} />
                  <AvatarFallback className="text-xs bg-locked">{(c.author?.display_name || "U")[0]}</AvatarFallback>
                </Avatar>
                {c.author?.username
                  ? <Link to={`/@${c.author.username}`} className="text-sm font-semibold hover:underline">@{c.author.username}</Link>
                  : <span className="text-sm font-semibold">{c.author?.display_name}</span>}
                <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                {user && user.user_id === c.user_id && (
                  <button onClick={() => remove(c.id)} data-testid={`comment-delete-${c.id}`} aria-label="Delete"><Trash2 className="w-4 h-4 text-missed" /></button>
                )}
                {user && user.user_id !== c.user_id && (
                  <button onClick={() => report(c.id)} aria-label="Report"><Flag className="w-4 h-4 text-muted-foreground" /></button>
                )}
              </div>
            </div>
            <p className="text-sm mt-1">{c.text}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No comments yet. Start the debate.</p>}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} title="Sign in to comment" />
    </div>
  );
}
